/**
 * The pre-trade checklist — ROADMAP 5.23.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE LAST THING BETWEEN A DECISION AND AN ORDER
 *
 * What loses money on a short-premium book is rarely a wrong strike; it is
 * entering on a day the trader would not have entered on if they had stopped
 * to ask. This is the stop. Every line is a human claim — nothing here is
 * verified by software, and a tick the system awarded itself would be a
 * claim it cannot support.
 *
 * IT CAN BE SKIPPED, AND THE SKIP IS RECORDED.
 *
 * A gate that cannot be skipped is a gate people tick without reading. So
 * "Skip for this entry" SENDS — one click, like the place button beside it —
 * and `checklistDone: false` goes to the server with it. Ticking everything
 * sends with `true`. Either way "which entries did I take without stopping"
 * is answerable afterwards instead of being a feeling.
 *
 * EDITS ARE SAVED TO THE STRATEGY, NOT TO THIS MODAL.
 *
 * `Customise` writes the list to the trader's own copy of the strategy, so
 * it is there on the next entry, on another machine, and in the journal.
 * An empty list is a legitimate save: it turns the gate off for that one
 * strategy.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useState } from 'react'
import { Check, Circle, Pencil } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { StrategyDto } from '@/types/strategy'
import { ChecklistEditor } from './ChecklistEditor'

/**
 * The two fields this reads off a strategy — ROADMAP 9.69.
 *
 * Narrowed from the whole `StrategyDto` because that is all it uses, and the
 * wider type kept a caller out that has every field it actually needs: a
 * basket opened by Adjustments carries the seeding strategy's id and its
 * checklist, and nothing else about it. A parameter should ask for what it
 * uses — the same rule `OrderAccount` follows for the order path.
 */
export type ChecklistSource = Pick<StrategyDto, 'id' | 'checklist'>

export interface PreTradeChecklistProps {
  strategy: ChecklistSource
  /** Reset whenever the modal reopens or the armed strategy changes. */
  resetKey: string
  /** True once every check is ticked. The place button reads this. */
  /**
   * `result` is the list AS SHOWN, with what is ticked — 9.8.
   *
   * The boolean alone could only ever answer "were all of them confirmed",
   * and the question a review actually asks is WHICH ones were not.
   */
  onReadyChange: (
    ready: boolean,
    checklistDone: boolean,
    result: readonly { rule: string; ticked: boolean }[],
  ) => void
  /**
   * Skip and send, in one click.
   *
   * It places rather than unlocking: a trader who has decided to go without
   * the checks has decided, and a second click on another button is
   * ceremony that teaches them to click twice without reading.
   */
  onSkip: () => void
  /** The saved strategy, so the caller can keep its copy current. */
  onStrategyChanged?: (strategy: StrategyDto) => void
}

export function PreTradeChecklist({
  strategy,
  resetKey,
  onReadyChange,
  onSkip,
  onStrategyChanged,
}: PreTradeChecklistProps) {
  /*
   * `?? []` because a terminal must not go blank. A DTO without the field —
   * an older cached bundle, a shape that changed under us — is a missing
   * checklist, which is a screen with no questions on it, not a crash on
   * the screen used to place orders.
   */
  const items = strategy.checklist ?? []
  const [ticked, setTicked] = useState<readonly number[]>([])
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /*
   * Ticks are per PLACEMENT. Carrying them across a close would mean a
   * trader confirms the checks once and every basket afterwards inherits
   * them — the same reason the typed LIVE word is cleared in the picker.
   */
  useEffect(() => {
    setTicked([])
    setEditing(false)
    setError(null)
  }, [resetKey])

  const done = useMemo(() => ticked.length, [ticked])
  const complete = items.length > 0 && done === items.length
  // An empty list is a deliberate "no questions here", so it gates nothing.
  const ready = items.length === 0 || complete

  const result = useMemo(
    () => items.map((rule, i) => ({ rule, ticked: ticked.includes(i) })),
    [items, ticked],
  )

  useEffect(() => {
    onReadyChange(ready, ready, result)
  }, [ready, result, onReadyChange])

  /**
   * Save, and hand the caller the UPDATED STRATEGY — unwrapped.
   *
   * `PATCH /strategies/:id` answers `{ strategy }`, like every other write
   * on this API. Passing the envelope up set the armed strategy to an object
   * with no `checklist`, `items.map` threw, and React unmounted the whole
   * tree: the terminal went white until a reload fetched a real one. The
   * tests did not catch it because the stub returned what this code
   * expected rather than what the server sends.
   */
  const save = async (next: string[]): Promise<void> => {
    setSaving(true)
    setError(null)
    try {
      const { strategy: updated } = await api.patch<{ strategy: StrategyDto }>(
        `/strategies/${strategy.id}`,
        { checklist: next },
      )
      onStrategyChanged?.(updated)
      setTicked([])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'The checklist could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="mt-5 border-t border-border pt-4" data-testid="pre-trade-checklist">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          {editing ? 'Customise — your list for this strategy' : 'Confirm each before entering'}
        </span>
        <div className="flex items-center gap-3">
          {/*
            NO "USE THE DEFAULT". The list is the trader's — the shipped
            questions are a starting point written into their own copy,
            and a button offering to throw their edits away for ours had
            no business on this screen.
          */}
          <button
            type="button"
            onClick={() => setEditing((e) => !e)}
            data-testid="checklist-edit-toggle"
            className="flex items-center gap-1 text-[10.5px] font-semibold text-muted-foreground hover:text-primary"
          >
            <Pencil size={11} /> {editing ? 'Done' : 'Customise'}
          </button>
        </div>
      </div>

      {items.length === 0 && !editing && (
        <p className="text-[12px] text-muted-foreground" data-testid="checklist-empty">
          No checks on this strategy. Customise to add some.
        </p>
      )}

      {editing ? (
        <ChecklistEditor
          items={items}
          busy={saving}
          onChange={(next) => void save(next)}
        />
      ) : (
        <ul className="space-y-1">
          {items.map((item, i) => (
            <li key={`${item}-${i}`}>
              <button
                type="button"
                data-testid={`checklist-item-${i}`}
                aria-pressed={ticked.includes(i)}
                onClick={() =>
                  setTicked((t) => (t.includes(i) ? t.filter((x) => x !== i) : [...t, i]))
                }
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                  ticked.includes(i)
                    ? 'border-success/40 bg-success/[0.07]'
                    : 'border-border bg-muted/20 hover:border-[var(--tr-line3)]',
                )}
              >
                {ticked.includes(i) ? (
                  <Check size={15} className="shrink-0 text-success" />
                ) : (
                  <Circle size={15} className="shrink-0 text-muted-foreground" />
                )}
                <span
                  className={cn(
                    'text-[12.5px] leading-snug',
                    ticked.includes(i) ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {item}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="mt-2 text-[11.5px] text-destructive" data-testid="checklist-error">
          {error}
        </p>
      )}

      {!editing && items.length > 0 && (
        <div className="mt-3 flex items-center gap-3">
          <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-success transition-[width]"
              style={{ width: `${(done / items.length) * 100}%` }}
            />
          </div>
          <span className="num text-[10.5px] text-muted-foreground" data-testid="checklist-progress">
            {done}/{items.length}
          </span>
          <span className="flex-1" />
          {!complete && (
            <button
              type="button"
              onClick={onSkip}
              data-testid="checklist-skip"
              title="Sends now, and records that the checks were skipped"
              className="text-[11px] font-semibold text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              Skip for this entry
            </button>
          )}
        </div>
      )}
    </section>
  )
}
